import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Result, Button } from 'antd';
import { useI18n } from '../i18n/I18nContext';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to error reporting service (e.g., Sentry)
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Don't log sensitive information
    if (error.message) {
      console.error('Error message:', error.message);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback 
          error={this.state.error}
          resetError={() => this.setState({ hasError: false, error: null })}
        />
      );
    }

    return this.props.children;
  }
}

const ErrorFallback: React.FC<{ error: Error | null; resetError: () => void }> = ({ error, resetError }) => {
  const { t } = useI18n();

  // Pas de useNavigate ici : l'ErrorBoundary est monté au-dessus du <Router> dans App.tsx,
  // donc le hook lèverait une erreur et le fallback planterait à son tour.
  const handleGoHome = () => {
    resetError();
    window.location.assign('/');
  };

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '100vh',
      padding: '2rem'
    }}>
      <Result
        status="error"
        title={t.app.errorBoundary.title}
        subTitle={t.app.errorBoundary.subtitle}
        extra={[
          <Button type="primary" key="home" onClick={handleGoHome}>
            {t.app.errorBoundary.goHome}
          </Button>,
          <Button key="reload" onClick={handleReload}>
            {t.app.errorBoundary.reload}
          </Button>,
        ]}
      >
        {process.env.NODE_ENV === 'development' && error && (
          <div style={{ 
            marginTop: '2rem', 
            padding: '1rem', 
            background: '#f5f5f5', 
            borderRadius: '8px',
            textAlign: 'left',
            maxWidth: '600px'
          }}>
            <strong>{t.app.errorBoundary.devDetails}</strong>
            <pre style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>
              {error.toString()}
            </pre>
          </div>
        )}
      </Result>
    </div>
  );
};

export default ErrorBoundary;
